import PptxGenJS from 'pptxgenjs'
import type { PlanData, LayoutConfig } from './dealCalculations'
import { defaultLayoutConfig } from './dealCalculations'
import { formatCurrency } from './format-currency'
import { appendInvestmentSlide } from './pptxAppendInvestmentSlide'
import { appendRocketStandardSlides } from './pptxAppendStandardSlides'
import { type BuildExportPlanDataOptions } from './proposalExport'
import { buildComboExportSlides, buildSeparateExportSlides } from './proposalExportComboSlides'

export { getTemplateType } from './pptxTemplateType'

const GREEN_DARK = '0F1E14'
const GREEN_ACCENT = '40C351'
const GRAY_LIGHT = 'F5F5F5'
const WHITE = 'FFFFFF'


export async function generateInvestmentPpt(
  plan: PlanData,
  layoutConfig: LayoutConfig = defaultLayoutConfig
): Promise<void> {
  const pptx = new PptxGenJS()
  pptx.layout = 'LAYOUT_WIDE'
  pptx.author = 'Conecta'
  pptx.title = `Proposta - ${plan.nome}`

  await appendRocketStandardSlides(pptx)
  appendInvestmentSlide(pptx, plan, layoutConfig)

  const fileName = `Proposta_${plan.nome.replace(/\s+/g, '_')}_${Date.now()}.pptx`
  await pptx.writeFile({ fileName })
}

// ─── MULTI-OPÇÃO: Capa Comparativa + Slides por Variação ────────────────────

import type { ComboVariationWithPricing, Proposta } from '@/types'

/**
 * Gera um PPTX multi-slide com:
 *   Slide 1: Capa comparativa com todas as variações lado a lado
 *   Slides 2+: Um slide de investimento por variação (reutiliza generateInvestmentPpt internamente)
 */
export async function generateComboVariationPpt(
  variations: ComboVariationWithPricing[],
  empresaNome: string
): Promise<void> {
  if (!variations || variations.length === 0) return

  const pptx = new PptxGenJS()
  pptx.layout = 'LAYOUT_WIDE'
  pptx.author = 'Conecta'
  pptx.title = `Proposta Comercial - ${empresaNome}`

  const slideW = 13.33
  const slideH = 7.5

  // ─── SLIDE 1: Capa Comparativa ──────────────────────────────────────────

  const coverSlide = pptx.addSlide()

  // Background escuro
  coverSlide.addShape(pptx.ShapeType.rect, {
    x: 0, y: 0, w: slideW, h: slideH,
    fill: { color: GREEN_DARK },
  })

  // Faixa verde no topo
  coverSlide.addShape(pptx.ShapeType.rect, {
    x: 0, y: 0, w: slideW, h: 1.2,
    fill: { color: GREEN_ACCENT, transparency: 10 },
  })

  // Logo / Título
  coverSlide.addText([
    { text: 'Proposta Comercial', options: { color: WHITE, fontSize: 28, bold: true } },
    { text: '.', options: { color: GREEN_ACCENT, fontSize: 28, bold: true } },
  ], { x: 0.5, y: 0.22, w: 7, h: 0.75, fontFace: 'Arial' })

  coverSlide.addText(empresaNome.toUpperCase(), {
    x: 0.5, y: 0.85, w: slideW - 1, h: 0.35,
    fontSize: 11, color: 'AACCAA', fontFace: 'Arial', bold: false,
  })

  // Cards por variação
  const cardCount = variations.length
  const cardGap = 0.3
  const cardTotalW = slideW - 1.0 // margens laterais
  const cardW = (cardTotalW - (cardCount - 1) * cardGap) / cardCount
  const cardStartX = 0.5
  const cardY = 1.6
  const cardH = 5.3

  variations.forEach((v, i) => {
    const cx = cardStartX + i * (cardW + cardGap)
    const hasDiscount = (v.descontoPercentual && v.descontoPercentual > 0) || v.precoFechado !== undefined
    const isRecommended = i === Math.floor(cardCount / 2) // destaque no card do meio

    // Card background
    coverSlide.addShape(pptx.ShapeType.roundRect, {
      x: cx, y: cardY, w: cardW, h: cardH,
      fill: { color: isRecommended ? '1a3d22' : '0d1a11' },
      line: { color: isRecommended ? GREEN_ACCENT : '2a3d2a', width: isRecommended ? 2 : 1 },
      rectRadius: 0.15,
    })

    // Badge "RECOMENDADO"
    if (isRecommended) {
      coverSlide.addShape(pptx.ShapeType.roundRect, {
        x: cx + cardW / 2 - 0.7, y: cardY - 0.25, w: 1.4, h: 0.35,
        fill: { color: GREEN_ACCENT }, rectRadius: 0.08,
      })
      coverSlide.addText('RECOMENDADO', {
        x: cx + cardW / 2 - 0.7, y: cardY - 0.25, w: 1.4, h: 0.35,
        fontSize: 7, bold: true, color: '000000', fontFace: 'Arial', align: 'center', valign: 'middle',
      })
    }

    // Nome da variação
    coverSlide.addText(v.nome.toUpperCase(), {
      x: cx + 0.15, y: cardY + 0.25, w: cardW - 0.3, h: 0.45,
      fontSize: 14, bold: true, color: WHITE, fontFace: 'Arial', align: 'center',
    })

    // Descrição
    if (v.descricao) {
      coverSlide.addText(v.descricao, {
        x: cx + 0.15, y: cardY + 0.75, w: cardW - 0.3, h: 0.55,
        fontSize: 8, color: 'AAAAAA', fontFace: 'Arial', align: 'center', valign: 'top',
      })
    }

    // Divider
    coverSlide.addShape(pptx.ShapeType.line, {
      x: cx + 0.2, y: cardY + 1.4, w: cardW - 0.4, h: 0,
      line: { color: '2a4a2a', width: 1 },
    })

    // Itens da variação
    v.items.slice(0, 6).forEach((item, idx) => {
      coverSlide.addText([
        { text: '✓  ', options: { color: GREEN_ACCENT, fontSize: 8, bold: true } },
        { text: item.descricao || '', options: { color: 'CCCCCC', fontSize: 8 } },
      ], {
        x: cx + 0.2, y: cardY + 1.55 + idx * 0.28, w: cardW - 0.4, h: 0.28,
        fontFace: 'Arial',
      })
    })
    if (v.items.length > 6) {
      coverSlide.addText(`+ ${v.items.length - 6} itens adicionais`, {
        x: cx + 0.2, y: cardY + 1.55 + 6 * 0.28, w: cardW - 0.4, h: 0.25,
        fontSize: 7, color: '666666', fontFace: 'Arial',
      })
    }

    // Preço original riscado (se houver desconto)
    if (hasDiscount && v.valorOriginal !== v.valorFinal) {
      coverSlide.addText(formatCurrency(v.valorOriginal), {
        x: cx + 0.15, y: cardY + cardH - 1.45, w: cardW - 0.3, h: 0.3,
        fontSize: 10, color: '666666', fontFace: 'Arial', align: 'center', strike: true,
      })
    }

    // Badge desconto
    if (v.descontoPercentual && v.descontoPercentual > 0) {
      coverSlide.addShape(pptx.ShapeType.roundRect, {
        x: cx + cardW / 2 - 0.5, y: cardY + cardH - 1.55, w: 1.0, h: 0.3,
        fill: { color: 'E35933' }, rectRadius: 0.08,
      })
      coverSlide.addText(`${v.descontoPercentual}% OFF`, {
        x: cx + cardW / 2 - 0.5, y: cardY + cardH - 1.55, w: 1.0, h: 0.3,
        fontSize: 8, bold: true, color: WHITE, fontFace: 'Arial', align: 'center', valign: 'middle',
      })
    }

    // Preço Final
    coverSlide.addText(formatCurrency(v.valorFinal), {
      x: cx + 0.15, y: cardY + cardH - 1.15, w: cardW - 0.3, h: 0.6,
      fontSize: 20, bold: true, color: GREEN_ACCENT, fontFace: 'Arial', align: 'center',
    })

    coverSlide.addText('/mês', {
      x: cx + 0.15, y: cardY + cardH - 0.65, w: cardW - 0.3, h: 0.3,
      fontSize: 9, color: '888888', fontFace: 'Arial', align: 'center',
    })
  })

  // Footer da capa
  coverSlide.addText('Escolha a opção ideal para o seu negócio', {
    x: 0, y: slideH - 0.5, w: slideW, h: 0.4,
    fontSize: 10, color: '555555', fontFace: 'Arial', align: 'center',
  })

  // ─── SLIDES 2+: Um slide por variação ─────────────────────────────────────

  for (const v of variations) {
    const varSlide = pptx.addSlide()
    const hasDiscount = (v.descontoPercentual && v.descontoPercentual > 0) || v.precoFechado !== undefined

    // Reutiliza o layout da PlanData existente
    const leftW = slideW * 0.35

    varSlide.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: leftW, h: slideH, fill: { color: GREEN_DARK } })
    varSlide.addShape(pptx.ShapeType.rect, { x: leftW, y: 0, w: slideW - leftW, h: slideH, fill: { color: GRAY_LIGHT } })
    varSlide.addShape(pptx.ShapeType.line, { x: leftW, y: 0, w: 0, h: slideH, line: { color: '333333', width: 1 } })

    // Título esquerdo
    varSlide.addText([
      { text: 'Investimentos', options: { color: WHITE, fontSize: 26, bold: true } },
      { text: '.', options: { color: GREEN_ACCENT, fontSize: 26, bold: true } },
    ], { x: 0.4, y: 0.2, w: leftW - 0.8, h: 0.8, fontFace: 'Arial' })

    // Badge de opção
    varSlide.addText(`OPÇÃO ${v.nome.toUpperCase()}`, {
      x: 0.4, y: 0.95, w: leftW - 0.8, h: 0.35,
      fontSize: 13, color: GREEN_ACCENT, bold: true, fontFace: 'Arial',
    })

    if (v.descricao) {
      varSlide.addText(v.descricao, {
        x: 0.4, y: 1.35, w: leftW - 0.8, h: 0.9,
        fontSize: 10, color: 'BBBBBB', fontFace: 'Arial',
      })
    }

    // Badge de desconto no lado esquerdo
    if (hasDiscount) {
      const badgeText = v.precoFechado !== undefined ? 'PREÇO ESPECIAL' : `${v.descontoPercentual}% OFF`
      varSlide.addShape(pptx.ShapeType.roundRect, {
        x: 0.35, y: 3.8, w: leftW - 0.7, h: 0.4,
        fill: { color: 'E35933' }, rectRadius: 0.08,
      })
      varSlide.addText(badgeText, {
        x: 0.35, y: 3.8, w: leftW - 0.7, h: 0.4,
        fontSize: 11, bold: true, color: WHITE, fontFace: 'Arial', align: 'center', valign: 'middle',
      })
    }

    // Preço Original riscado
    if (hasDiscount && v.valorOriginal !== v.valorFinal) {
      varSlide.addText(formatCurrency(v.valorOriginal), {
        x: 0.4, y: 5.6, w: leftW - 0.8, h: 0.35,
        fontSize: 14, color: '666666', fontFace: 'Arial', strike: true,
      })
    }

    // Preço final
    varSlide.addText(`${formatCurrency(v.valorFinal)}/mês`, {
      x: 0.4, y: hasDiscount ? 5.95 : 5.8, w: leftW - 0.8, h: 0.7,
      fontSize: 24, color: GREEN_ACCENT, bold: true, fontFace: 'Arial',
    })

    // Nome do plano / empresa
    varSlide.addText(`${v.nome} — ${empresaNome}`, {
      x: leftW + 0.4, y: 0.2, w: (slideW - leftW) - 0.8, h: 0.5,
      fontSize: 18, color: '222222', bold: true, fontFace: 'Arial',
    })

    // Itens da variação em 2 colunas
    const items = v.items
    const rightStartX = leftW + 0.4
    const rightW = (slideW - leftW) - 0.8
    const halfW = rightW / 2
    const startY = 0.9

    items.forEach((item, idx) => {
      const col = idx % 2
      const row = Math.floor(idx / 2)
      const ix = rightStartX + col * (halfW + 0.2)
      const iy = startY + row * 0.32

      varSlide.addText([
        { text: '✓  ', options: { color: GREEN_ACCENT, fontSize: 9, bold: true } },
        { text: item.descricao || '', options: { color: '454545', fontSize: 9 } },
      ], { x: ix, y: iy, w: halfW - 0.1, h: 0.32, fontFace: 'Arial' })
    })
  }

  const fileName = `Proposta_MultiOpcao_${empresaNome.replace(/\s+/g, '_')}_${Date.now()}.pptx`
  await pptx.writeFile({ fileName })
}

/**
 * Vários itens independentes: um slide por plano, cada um com preço (como no preview).
 */
export async function generateMultiPlanProposalPpt(
  proposal: Proposta,
  empresaNome: string,
  options: BuildExportPlanDataOptions = {},
): Promise<void> {
  const pptx = new PptxGenJS()
  pptx.layout = 'LAYOUT_WIDE'
  pptx.author = 'Conecta'
  pptx.title = `Proposta Comercial - ${empresaNome}`

  await appendRocketStandardSlides(pptx)
  const slides = buildSeparateExportSlides(proposal, options)
  for (const entry of slides) {
    appendInvestmentSlide(pptx, entry.plan, entry.layout, { hidePricing: entry.hidePricing })
  }

  const fileName = `Proposta_${empresaNome.replace(/\s+/g, '_')}_${Date.now()}.pptx`
  await pptx.writeFile({ fileName })
}

/**
 * Combo: um slide padrão de investimento por plano do combo.
 * O valor (preço "por") aparece somente no último slide.
 */
export async function generateComboProposalPpt(
  proposal: Proposta,
  empresaNome: string,
  options: BuildExportPlanDataOptions = {},
): Promise<void> {
  const pptx = new PptxGenJS()
  pptx.layout = 'LAYOUT_WIDE'
  pptx.author = 'Conecta'
  pptx.title = `Proposta Comercial - ${empresaNome}`

  await appendRocketStandardSlides(pptx)
  const slides = buildComboExportSlides(proposal, options)
  for (const entry of slides) {
    appendInvestmentSlide(pptx, entry.plan, entry.layout, { hidePricing: entry.hidePricing })
  }

  const fileName = `Proposta_Combo_${empresaNome.replace(/\s+/g, '_')}_${Date.now()}.pptx`
  await pptx.writeFile({ fileName })
}
